import { DomainValidationError } from "./identifiers.js";
import { isCrossCityPair, MIN_CROSS_CITY_GAP_MINUTES } from "./itinerary-cross-city.js";
import { DEFAULT_TRAVEL_MODE, staticTravelEstimate, } from "./travel-mode.js";
export const DEFAULT_FEASIBILITY_POLICY = Object.freeze({
    bufferMinutes: Object.freeze({ WALK: 10, DRIVE: 10, TRANSIT: 15 }),
    arrivalMarginMinutes: 15,
    tightFactor: 1.25,
    toleranceMinutes: 0,
    crossCityMinimumMinutes: MIN_CROSS_CITY_GAP_MINUTES,
});
export const edgeStatuses = Object.freeze([
    "FEASIBLE",
    "TIGHT",
    "INFEASIBLE",
    "UNVERIFIED",
]);
/** Why an edge has no travel verdict. Closed codes: a database CHECK and the customer wording are built from this tuple. */
export const edgeUnverifiedReasons = Object.freeze([
    "LOCATION_UNKNOWN",
    "PROVIDER_UNAVAILABLE",
    "QUOTA",
    "TIMEOUT",
    "NO_ROUTE",
    "INVALID_REQUEST",
    "NOT_CONFIGURED",
    "TRANSIT_OUT_OF_RANGE",
    "BUDGET",
    "STATIC_HEURISTIC",
    "NO_ESTIMATE",
    "NOT_CHECKED",
    /** The previous item has neither an end nor a duration: the gap is unknown, never zero. */
    "END_UNKNOWN",
    /** A TRANSIT or BUFFER item between the two has no known duration: it cannot stand for the travel. */
    "CONNECTOR_DURATION_UNKNOWN",
]);
export const edgeDecidedReasons = Object.freeze([
    "OK",
    "TRAVEL_TIGHT",
    "TRAVEL_TIME_EXCEEDS_GAP",
    "OVERLAP",
    "CROSS_CITY_MINIMUM",
    "COVERED_BY_TRANSIT",
    "COVERED_BY_BUFFER",
    "SAME_PLACE",
]);
/** The basis of a stored estimate. Frozen: the database CHECK of `itinerary_route_evidence` is generated from this tuple. */
export const edgeEstimateBases = Object.freeze([
    "TRAFFIC_UNAWARE",
    "TRAFFIC_AWARE",
    "STATIC_HEURISTIC",
]);
/** `VERIFIED` is provider data; `UNVERIFIED` is an observation that has a reason and never a claim. Frozen like the tuple above. */
export const routeEvidenceStates = Object.freeze(["VERIFIED", "UNVERIFIED"]);
const MINUTE_MS = 60_000;
const validInstant = (value) => value instanceof Date && !Number.isNaN(value.getTime());
const isConnector = (item) => item.kind === "TRANSIT" || item.kind === "BUFFER";
function endOf(item) {
    if (!validInstant(item.startsAt))
        return null;
    if (validInstant(item.endsAt))
        return item.endsAt.getTime() >= item.startsAt.getTime() ? item.endsAt : null;
    if (item.durationMinutes !== null) {
        return new Date(item.startsAt.getTime() + item.durationMinutes * MINUTE_MS);
    }
    // No end and no duration: the end is UNKNOWN. It is never taken as the start (a known zero).
    return null;
}
const sameWaypoint = (a, b) => a !== null &&
    b !== null &&
    (a.kind === "PLACE_REF" && b.kind === "PLACE_REF"
        ? a.placeId === b.placeId
        : a.kind === "POINT" && b.kind === "POINT"
            ? a.latitude === b.latitude && a.longitude === b.longitude
            : false);
const edgeIdOf = (from, to) => `${from.key}>${to.key}`;
/** The status of a gap against the time that is required (contract 5.1): `INFEASIBLE` under the required time, `TIGHT` under 1.25 times it. */
function judge(gapMinutes, requiredMinutes, policy) {
    if (gapMinutes < requiredMinutes - policy.toleranceMinutes) {
        return { status: "INFEASIBLE", reasonCode: "TRAVEL_TIME_EXCEEDS_GAP" };
    }
    if (gapMinutes < requiredMinutes * policy.tightFactor) {
        return { status: "TIGHT", reasonCode: "TRAVEL_TIGHT" };
    }
    return { status: "FEASIBLE", reasonCode: "OK" };
}
/**
 * The edges of a plan: for each day section, the scheduled items ordered by start time, and one edge for each pair of consecutive
 * route-checked items (not cancelled, not a TRANSIT or BUFFER item). A TRANSIT or BUFFER item between two of them covers the edge: its
 * own length stands for the travel and no estimate is needed. An edge that a rule decides without an estimate carries `decided`.
 *
 * Decision order: an unknown end, an overlap, the cross-city minimum, a planned connector, the same place, an unknown location. What is
 * left needs an estimate.
 *
 * Unknown stays unknown. A previous item with neither an end nor a duration makes the edge `UNVERIFIED` (`END_UNKNOWN`), never a
 * zero-length item. A connector with no known duration cannot stand for the travel: `UNVERIFIED` (`CONNECTOR_DURATION_UNKNOWN`).
 *
 * Which buffers a connector covers: a planned TRANSIT or BUFFER item stands for the travel AND the mode buffer (10 or 15 minutes): it is
 * the time the plan sets aside between the two items. It does not cover the arrival margin before a reservation or an event: that
 * margin (15 minutes) is added to the connector's length. The TIGHT threshold applies. A same-place edge has zero travel and then the
 * normal buffers (the mode buffer and the arrival margin) and thresholds.
 */
export function routeEdgesOf(items, policy = DEFAULT_FEASIBILITY_POLICY, modeFor = () => DEFAULT_TRAVEL_MODE) {
    const bySection = new Map();
    for (const item of items) {
        if (item.daySectionKey === null || item.timingKind !== "SCHEDULED")
            continue;
        if (item.status === "CANCELLED" || !validInstant(item.startsAt))
            continue;
        const list = bySection.get(item.daySectionKey) ?? [];
        list.push(item);
        bySection.set(item.daySectionKey, list);
    }
    const edges = [];
    for (const [sectionKey, list] of bySection) {
        const ordered = [...list].sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime() ||
            (endOf(a) ?? a.startsAt).getTime() -
                (endOf(b) ?? b.startsAt).getTime() ||
            a.position - b.position ||
            (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
        let previous = null;
        let connectors = [];
        for (const item of ordered) {
            if (isConnector(item)) {
                connectors.push(item);
                continue;
            }
            if (previous !== null) {
                edges.push(edgeOf(previous, item, connectors, sectionKey, policy, modeFor(previous, item)));
            }
            previous = item;
            connectors = [];
        }
    }
    return edges;
}
const connectorMinutes = (connector) => {
    const end = endOf(connector);
    return end === null ? null : (end.getTime() - connector.startsAt.getTime()) / MINUTE_MS;
};
function edgeOf(from, to, connectors, daySectionKey, policy, mode) {
    const departure = endOf(from);
    const gapMinutes = departure === null ? null : (to.startsAt.getTime() - departure.getTime()) / MINUTE_MS;
    const margin = to.needsArrivalMargin ? policy.arrivalMarginMinutes : 0;
    const bufferMinutes = policy.bufferMinutes[mode] + margin;
    const lengths = connectors.map(connectorMinutes);
    const base = {
        id: edgeIdOf(from, to),
        fromKey: from.key,
        toKey: to.key,
        fromLineageId: from.lineageId,
        toLineageId: to.lineageId,
        daySectionKey,
        mode,
        gapMinutes,
        bufferMinutes,
        fromWaypoint: from.waypoint,
        toWaypoint: to.waypoint,
        departure,
        coverage: connectors
            .map((connector, index) => `${connector.lineageId ?? connector.key}:${lengths[index] ?? "?"}`)
            .join(","),
    };
    const decide = (status, reasonCode, requiredMinutes, travelMinutes, buffer = bufferMinutes) => ({
        ...base,
        bufferMinutes: buffer,
        decided: { status, reasonCode, requiredMinutes, travelMinutes },
    });
    // The end of the previous item is not known: the gap is not known. No number is made up.
    if (gapMinutes === null)
        return decide("UNVERIFIED", "END_UNKNOWN", null, null);
    // Hard conflicts that need no provider hold whatever a provider would say.
    if (gapMinutes < 0)
        return decide("INFEASIBLE", "OVERLAP", null, null);
    // A planned TRANSIT or BUFFER item between the two replaces the cross-city check, as it does in `validateItinerary`.
    if (connectors.length === 0 &&
        isCrossCityPair(from, to) &&
        gapMinutes < policy.crossCityMinimumMinutes) {
        return decide("INFEASIBLE", "CROSS_CITY_MINIMUM", policy.crossCityMinimumMinutes, null);
    }
    if (connectors.length > 0) {
        if (lengths.some((length) => length === null)) {
            return decide("UNVERIFIED", "CONNECTOR_DURATION_UNKNOWN", null, null);
        }
        // The planned item is the travel and the mode buffer. The arrival margin before a reservation or an event is still needed.
        const minutes = lengths.reduce((sum, length) => sum + length, 0);
        const required = minutes + margin;
        const verdict = judge(gapMinutes, required, policy);
        return decide(verdict.status, verdict.status === "FEASIBLE"
            ? connectors.every((connector) => connector.kind === "BUFFER")
                ? "COVERED_BY_BUFFER"
                : "COVERED_BY_TRANSIT"
            : verdict.reasonCode, required, minutes, margin);
    }
    if (sameWaypoint(from.waypoint, to.waypoint)) {
        // No travel, and then the normal buffers and thresholds.
        const verdict = judge(gapMinutes, bufferMinutes, policy);
        return decide(verdict.status, verdict.status === "FEASIBLE" ? "SAME_PLACE" : verdict.reasonCode, bufferMinutes, 0);
    }
    if (from.waypoint === null || to.waypoint === null) {
        return decide("UNVERIFIED", "LOCATION_UNKNOWN", null, null);
    }
    return { ...base, decided: null };
}
/** The edges that need a provider (or a stored) estimate: no rule decided them. */
export const edgesNeedingEstimate = (edges) => edges.filter((edge) => edge.decided === null);
function assessWithEstimate(edge, estimate, policy) {
    const common = {
        edgeId: edge.id,
        fromKey: edge.fromKey,
        toKey: edge.toKey,
        fromLineageId: edge.fromLineageId,
        toLineageId: edge.toLineageId,
        mode: edge.mode,
        gapMinutes: edge.gapMinutes,
        bufferMinutes: edge.bufferMinutes,
    };
    if (!estimate.ok) {
        return {
            ...common,
            requiredMinutes: null,
            travelMinutes: null,
            status: "UNVERIFIED",
            reasonCode: estimate.reasonCode,
            basis: null,
            evidenceRef: estimate.evidenceRef ?? null,
            observedAt: null,
        };
    }
    const travelMinutes = estimate.durationSeconds / 60;
    const requiredMinutes = Math.ceil(travelMinutes) + edge.bufferMinutes;
    if (estimate.basis === "STATIC_HEURISTIC") {
        // A heuristic is shown, never trusted: it cannot refuse a plan and it is never reported as verified.
        return {
            ...common,
            requiredMinutes,
            travelMinutes,
            status: "UNVERIFIED",
            reasonCode: "STATIC_HEURISTIC",
            basis: estimate.basis,
            evidenceRef: estimate.evidenceRef,
            observedAt: estimate.observedAt,
        };
    }
    // An edge that needs an estimate has a known end, so its gap is known.
    const { status, reasonCode } = judge(edge.gapMinutes, requiredMinutes, policy);
    return {
        ...common,
        requiredMinutes,
        travelMinutes,
        status,
        reasonCode,
        basis: estimate.basis,
        evidenceRef: estimate.evidenceRef,
        observedAt: estimate.observedAt,
    };
}
/**
 * The verdict of every edge of a plan. `estimates` is keyed by edge id; an edge that needs one and has none is `UNVERIFIED` with
 * `NOT_CHECKED` (the application did not look: nothing is invented).
 */
export function assessRouteEdges(input) {
    const policy = input.policy ?? DEFAULT_FEASIBILITY_POLICY;
    return assessEdges(routeEdgesOf(input.items, policy, input.modeFor), input.estimates, policy);
}
/** Like `assessRouteEdges`, for edges that were already built (the application builds them once to know which need an estimate). */
export function assessEdges(edges, estimates, policy = DEFAULT_FEASIBILITY_POLICY) {
    return edges.map((edge) => {
        if (edge.decided !== null) {
            return {
                edgeId: edge.id,
                fromKey: edge.fromKey,
                toKey: edge.toKey,
                fromLineageId: edge.fromLineageId,
                toLineageId: edge.toLineageId,
                mode: edge.mode,
                gapMinutes: edge.gapMinutes,
                requiredMinutes: edge.decided.requiredMinutes,
                bufferMinutes: edge.bufferMinutes,
                travelMinutes: edge.decided.travelMinutes,
                status: edge.decided.status,
                reasonCode: edge.decided.reasonCode,
                basis: null,
                evidenceRef: null,
                observedAt: null,
            };
        }
        const estimate = estimates.get(edge.id);
        return assessWithEstimate(edge, estimate ?? { ok: false, reasonCode: "NOT_CHECKED" }, policy);
    });
}
/** The static heuristic for an edge whose two ends are both Lafayette-owned points; `null` when a place is only a provider id (no coordinate is kept). */
export function staticEdgeEstimate(edge) {
    if (edge.fromWaypoint?.kind !== "POINT" || edge.toWaypoint?.kind !== "POINT")
        return null;
    const from = edge.fromWaypoint;
    const to = edge.toWaypoint;
    return staticTravelEstimate(from, to, edge.mode);
}
/**
 * `TRAVEL_TIME_EXCEEDS_GAP` (BLOCKING) for an infeasible edge, `TRAVEL_TIGHT` (WARNING) for a tight one, `ROUTE_UNVERIFIED` (WARNING)
 * for an edge without a verdict. An edge that was not looked at (`NOT_CHECKED`) raises nothing: it is not part of this change.
 * `crossCity: "SKIP"` leaves out the edges that the cross-city minimum decided (`validateItinerary` raises that issue itself).
 */
export function routeIssuesOf(assessments, options = {}) {
    const issues = [];
    for (const edge of assessments) {
        // `validateItinerary` raises the cross-city issue itself (apply, activation and every validate route): a caller that runs it too
        // skips it here, so the issue is raised once.
        if (options.crossCity === "SKIP" && edge.reasonCode === "CROSS_CITY_MINIMUM")
            continue;
        const at = { edgeId: edge.edgeId, toKey: edge.toKey, toLineageId: edge.toLineageId };
        if (edge.status === "INFEASIBLE") {
            issues.push({ code: "TRAVEL_TIME_EXCEEDS_GAP", severity: "BLOCKING", ...at });
        }
        else if (edge.status === "TIGHT") {
            issues.push({ code: "TRAVEL_TIGHT", severity: "WARNING", ...at });
        }
        else if (edge.status === "UNVERIFIED" && edge.reasonCode !== "NOT_CHECKED") {
            issues.push({ code: "ROUTE_UNVERIFIED", severity: "WARNING", ...at });
        }
    }
    return issues;
}
/**
 * The feasibility a report holds (contract 5.1): `INFEASIBLE` when any edge is, `UNKNOWN` unless every edge is verified, `FEASIBLE`
 * when every edge is `FEASIBLE` or `TIGHT`. A plan with no edge to check is `UNKNOWN`: nothing was judged.
 */
export function routeFeasibilityOf(assessments) {
    if (assessments.some((edge) => edge.status === "INFEASIBLE"))
        return "INFEASIBLE";
    if (assessments.length === 0)
        return "UNKNOWN";
    return assessments.every((edge) => edge.status === "FEASIBLE" || edge.status === "TIGHT")
        ? "FEASIBLE"
        : "UNKNOWN";
}
// --- Candidate sequencing --------------------------------------------------------------------------------------------------
export const MAX_CANDIDATE_ORDERS = 6;
export const MAX_ITEMS_PER_CANDIDATE_ORDER = 6;
export const legKey = (fromKey, toKey) => `${fromKey}>${toKey}`;
/**
 * Compares candidate orders of the same places by total travel time and returns them best first, with the numbers. It never ranks by
 * catalog fit: editorial ranking stays with the catalog search. `estimates` (seconds) is keyed by `legKey(from, to)`. An order with a
 * missing leg is incomplete and goes after every complete one (ties keep the input order).
 */
export function rankCandidateOrders(input) {
    if (input.candidates.length > MAX_CANDIDATE_ORDERS) {
        throw new DomainValidationError("candidates", `holds at most ${MAX_CANDIDATE_ORDERS} orders`);
    }
    const ranked = input.candidates.map((candidate, index) => {
        if (candidate.itemKeys.length > MAX_ITEMS_PER_CANDIDATE_ORDER) {
            throw new DomainValidationError("candidates", `an order holds at most ${MAX_ITEMS_PER_CANDIDATE_ORDER} items`);
        }
        const legs = candidate.itemKeys.slice(1).map((toKey, at) => {
            const fromKey = candidate.itemKeys[at];
            return { fromKey, toKey, travelSeconds: input.estimates.get(legKey(fromKey, toKey)) ?? null };
        });
        const complete = legs.every((leg) => leg.travelSeconds !== null);
        const order = {
            id: candidate.id,
            legs,
            complete,
            totalTravelSeconds: complete
                ? legs.reduce((sum, leg) => sum + leg.travelSeconds, 0)
                : null,
        };
        return { order, index };
    });
    return ranked
        .sort((a, b) => Number(!a.order.complete) - Number(!b.order.complete) ||
        (a.order.totalTravelSeconds ?? 0) - (b.order.totalTravelSeconds ?? 0) ||
        a.index - b.index)
        .map((entry) => entry.order);
}
